/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, computed, inject, input, output } from '@angular/core';
import { TaskViewVoModel } from '@well/delivery-libs/task-management/domain';
import { SharedComponentsModule } from '@well/shared-components';
import { TranslateModule } from '@ngx-translate/core';
import { TaskCardSummaryDetailsComponent } from '../task-card-summary-details/task-card-summary-details.component';
import { TaskCardCheckpointComponent } from './task-card-checkpoint/task-card-checkpoint.component';
import { translationSchema } from '@well/translation';
import { UpperCasePipe } from '@angular/common';
import { dispatcherTaskStatusChangeUrl } from '@well/navigation';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import {
  BsPopoverComponent,
  BsButtonComponent,
  BsButtonDirective,
  BsButtonIconDirective,
  BsButtonLabelDirective,
} from '@well/iriro-frontend-libs/bs-component-store';
import { TaskCardSummaryService } from './task-card-summary.service';
import { TaskDetailsDialogComponent } from '../task-details-dialog/task-details-dialog.component';
import { TaskDeleteDialogComponent } from '../task-delete-dialog/task-delete-dialog.component';
import { TaskCloseDialogComponent } from '../task-close-dialog/task-close-dialog.component';
import { TaskFullHandoverDialogComponent } from '../task-full-handover-dialog/task-full-handover-dialog.component';
@Component({
    selector: 'well-task-card-summary',
    templateUrl: './task-card-summary.component.html',
    styleUrls: ['./task-card-summary.component.scss'],
    imports: [
        SharedComponentsModule,
        TranslateModule,
        TaskCardSummaryDetailsComponent,
        TaskCardCheckpointComponent,
        UpperCasePipe,
        BsButtonComponent,
        BsButtonDirective,
        BsButtonIconDirective,
        BsButtonLabelDirective,
        TooltipModule,
        TaskDetailsDialogComponent,
        TaskDeleteDialogComponent,
        TaskCloseDialogComponent,
        TaskFullHandoverDialogComponent,
        BsPopoverComponent
    ]
})
export class TaskCardSummaryComponent {
  task = input.required<TaskViewVoModel>();
  solutionDeleted = output<void>();

  sourceLocation = computed(() => {
    const task = this.task();
    if (task.source) {
      if (task.source.city && task.source.country) {
        return task.source.city + ', ' + task.source.country;
      } else {
        return task.source.name;
      }
    }
    return undefined;
  });

  returnLocation = computed(() => {
    const task = this.task();
    if (task.returnCheckpoint) {
      return {
        uuid: task.returnCheckpoint.uuid,
        location:
          task.returnCheckpoint.locationDTO?.city && task.returnCheckpoint.locationDTO?.country
            ? task.returnCheckpoint.locationDTO.city + ', ' + task.returnCheckpoint.locationDTO.country
            : task.returnCheckpoint.locationDTO?.name,
        namedLocationCode: task.returnCheckpoint.namedLocationCode,
        namedLocationLabel: task.returnCheckpoint.namedLocationLabel,
        timeWindowStart: task.returnCheckpoint.timeWindowStart,
        timeWindowEnd: task.returnCheckpoint.timeWindowEnd,
        state: task.returnCheckpoint.state,
      };
    }
    return undefined;
  });

  destLocation = computed(() => {
    const task = this.task();

    if (task.deliveryCheckpoints) {
      return task.deliveryCheckpoints.map((item) => {
        return {
          uuid: item.uuid,
          location:
            item.locationDTO?.city && item.locationDTO?.country
              ? item.locationDTO.city + ', ' + item.locationDTO.country
              : item.locationDTO?.name,
          namedLocationCode: item.namedLocationCode,
          namedLocationLabel: item.namedLocationLabel,
          timeWindowStart: item.timeWindowStart,
          timeWindowEnd: item.timeWindowEnd,
          state: item.state,
        };
      });
    }
    return [];
  });

  service = inject(TaskCardSummaryService);
  router = inject(Router);
  protected readonly translationSchema = translationSchema;

  onTaskCheckpointChangeStatus(uuid: string) {
    this.router.navigate(dispatcherTaskStatusChangeUrl(uuid));
  }

  onUnavailableDelivery(uuid: string) {
    this.service.unavailable(uuid).subscribe(() => {
      this.refresh();
    });
  }

  onTaskDeleted() {
    this.refresh();
  }

  onSolutionDeleted() {
    this.solutionDeleted.emit();
  }

  onTaskClosed() {
    this.refresh();
  }

  onTaskHandoverFull() {
    this.refresh();
  }

  private refresh() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }
}
